import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    FormControl,
    FormHelperText
} from '@material-ui/core';
import DialogButton from '../Common/UI/Dialog/DialogButton';
import TextField from '../Common/UI/TextField';
import { useDispatch } from 'react-redux';
import {
    clearUserErrors,
    updateUserEmailAsync,
    updateUserPasswordAsync
} from '../../redux/user/Actions';
import { useSelector } from 'react-redux';
import { StoreState } from '../../redux/Store';

interface IConfirmEmailDialogProps {
    open: boolean;
    onClose: () => void;
    email?: string;
    isPasswordChange: boolean;
    setIsPasswordChange: (value: boolean) => void;
}
const defaultProps = {};

function ConfirmEmailPasswordDialog({
    open,
    onClose,
    email,
    isPasswordChange,
    setIsPasswordChange
}: IConfirmEmailDialogProps) {
    const dispatch = useDispatch();
    const user = useSelector((state: StoreState) => state.user);
    const [confirmState, setConfirmState] = useState({
        currentPassword: '',
        newPassword: '',
        passwordConfirmation: ''
    });

    function handleChange(target: HTMLInputElement) {
        setConfirmState(p => ({ ...p, [target.name]: target.value }));
        dispatch(clearUserErrors());
    }

    const isValid = useMemo(() => {
        if (isPasswordChange) {
            return (
                confirmState.currentPassword &&
                confirmState.newPassword &&
                confirmState.newPassword === confirmState.passwordConfirmation
            );
        }
        return confirmState.currentPassword;
    }, [confirmState, isPasswordChange]);

    const isConfirmed = useMemo(
        () => confirmState.newPassword === confirmState.passwordConfirmation,
        [confirmState.newPassword, confirmState.passwordConfirmation]
    );

    function handleSubmit() {
        if (isPasswordChange) {
            dispatch(updateUserPasswordAsync(confirmState, user?.authenticityToken, onClose));
        } else {
            dispatch(
                updateUserEmailAsync(
                    { email: email ?? '', authenticityToken: user?.authenticityToken },
                    confirmState.currentPassword,
                    onClose
                )
            );
        }
    }

    useEffect(() => {
        return () => {
            setIsPasswordChange(false);
        };
    }, []);

    return (
        <Dialog open={open} onClose={onClose} fullWidth>
            <DialogTitle disableTypography>
                <Typography variant="h4">
                    Change
                    {isPasswordChange ? ' Password' : ' Email'}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                {user.errors?.email && <FormHelperText error>{user.errors?.email}</FormHelperText>}
                <TextField
                    label="Password"
                    type="password"
                    name="currentPassword"
                    placeholder={isPasswordChange ? '' : 'Confirm your password to change email'}
                    fullWidth
                    value={confirmState.currentPassword}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e.target)}
                    errorMessage={user?.errors?.current_password ? 'Incorrect Password' : ''}
                />
                {isPasswordChange && (
                    <>
                        <TextField
                            label="New Password"
                            type="password"
                            name="newPassword"
                            fullWidth
                            value={confirmState.newPassword}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e.target)}
                            errorMessage={user?.errors?.password}
                        />
                        <TextField
                            label="Confirm Password"
                            type="password"
                            name="passwordConfirmation"
                            fullWidth
                            value={confirmState.passwordConfirmation}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e.target)}
                            errorMessage={
                                !isConfirmed && confirmState.newPassword
                                    ? "Passwords don't match"
                                    : ''
                            }
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <DialogButton
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!isValid || !isConfirmed}>
                    Submit
                </DialogButton>
                <DialogButton onClick={onClose} variant="outlined">
                    Cancel
                </DialogButton>
            </DialogActions>
        </Dialog>
    );
}

ConfirmEmailPasswordDialog.defaultProps = defaultProps;

export default ConfirmEmailPasswordDialog;
